import { downloadZip, InputWithoutMeta } from 'client-zip';
import { SingletonPromiseFactory } from './synPromise';
import mime from 'mime';
import nProgress from 'nprogress';
import { valueWithFallbacks } from '@/common/js/utils';
import { loadSecureMediaAsBlob } from '@/service/secureMedia/secureMedia';
import { swReadyState } from '@/service/secureMedia/swReadyState';

export interface IDownloadEntry {
    name?: string;
    url: string;
}

export type TDownloadEntryMap = Record<string, IDownloadEntry>;

const getFileTypeFromUrl = ( url: string ): string => {

    if ( url.startsWith( 'blob:' )) {

        return 'mp4';

    }

    const match = /(?:\.([^.?#]+))?(?:[?#]|$)/.exec( url );
    return match ? match[1] || '' : '';

};

const getFileNameFromHeader = ( headers: Record<string, string> ): string => {

    return headers['content-disposition']?.split( 'filename=' )[1] ?? '';

};

const blobToArrayBuffer = ( blob: Blob ): Promise<ArrayBuffer> => {

    if ( typeof blob.arrayBuffer === 'function' ) {

        return blob.arrayBuffer();

    }
    return new Promise(( resolve, reject ) => {

        const reader = new FileReader();
        reader.onload = () => {

            resolve( reader.result as ArrayBuffer );

        };
        reader.onerror = () => {

            reject( reader.error );

        };
        reader.readAsArrayBuffer( blob );

    });

};

const headersToRecord = ( headers: Headers ): Record<string, string> => {

    const result: Record<string, string> = {};
    for ( const [key, value] of headers ) {

        result[key.toLowerCase()] = value;

    }
    return result;

};

export class MultiFileDownloader {

    private readonly maxRetries = 5; // 最大重试次数

    private useSecureMediaFallback: boolean | null = null;

    public constructor( private urls: TDownloadEntryMap, private readonly getNewUrls: () => Promise<TDownloadEntryMap> ) {

    }

    private async shouldUseSecureMediaFallback(): Promise<boolean> {

        if ( this.useSecureMediaFallback !== null ) {

            return this.useSecureMediaFallback;

        }
        this.useSecureMediaFallback = !( await swReadyState.await( 1000 ));
        return this.useSecureMediaFallback;

    }

    private async downloadSecureMediaBlob( id: string, file: IDownloadEntry ): Promise<{ name: string; blob: Blob; }> {

        const { blob, contentDisposition, mime: mimeType } = await loadSecureMediaAsBlob( file.url );
        const headers = contentDisposition
            ? { 'content-disposition': contentDisposition }
            : {};

        return {
            name: this.getFileName(
                id,
                file,
                headers,
                mimeType
            ),
            blob,
        };

    }

    private async downloadSecureMediaFile( id: string, file: IDownloadEntry ): Promise<InputWithoutMeta> {

        const { name, blob } = await this.downloadSecureMediaBlob( id, file );

        return {
            name,
            input: await blobToArrayBuffer( blob ),
        } as InputWithoutMeta;

    }

    private async downloadSingleFile( id: string, retryCount = 0 ): Promise<InputWithoutMeta> {

        const file = this.urls[id];
        if ( !file ) {

            throw new Error( `No download entry found for id ${id}.` );

        }

        try {

            if ( await this.shouldUseSecureMediaFallback()) {

                return await this.downloadSecureMediaFile( id, file );

            }
            const response = await fetch( file.url );
            if ( !response.ok ) {

                throw new Error( `HTTP ${response.status}: ${response.statusText}` );

            }
            const data = await response.arrayBuffer();
            const headers = headersToRecord( response.headers );
            return {

                name: this.getFileName( id, file, headers ),
                input: data,

            };

        } catch ( e ) {

            console.warn( `Error downloading file from ${file.url}:`, e );
            if ( retryCount >= this.maxRetries ) {

                throw e;

            }
            await this.getNewFileEntry( id );
            return this.downloadSingleFile( id, retryCount + 1 );

        }

    }

    private async downloadSingleFileAsBlob( id: string, retryCount = 0 ): Promise<{ name: string; blob: Blob; }> {

        const file = this.urls[id];
        if ( !file ) {

            throw new Error( `No download entry found for id ${id}.` );

        }

        try {

            if ( await this.shouldUseSecureMediaFallback()) {

                return await this.downloadSecureMediaBlob( id, file );

            }
            const response = await fetch( file.url );
            if ( !response.ok ) {

                throw new Error( `HTTP ${response.status}: ${response.statusText}` );

            }
            const blob = await response.blob();
            const headers = headersToRecord( response.headers );
            return {

                name: this.getFileName( id, file, headers ),
                blob,

            };

        } catch ( e ) {

            console.warn( `Error downloading file from ${file.url}:`, e );
            if ( retryCount >= this.maxRetries ) {

                throw e;

            }
            await this.getNewFileEntry( id );
            return this.downloadSingleFileAsBlob( id, retryCount + 1 );

        }

    }

    private async getNewFileEntry( id: string ): Promise<IDownloadEntry> {

        await this.updateUrls();

        const file = this.urls[id];
        if ( !file ) {

            throw new Error( `No download entry found for id ${id} after refreshing URLs.` );

        }

        return file;

    }

    private readonly updateUrls = SingletonPromiseFactory( async (): Promise<void> => {

        const newUrls = await this.getNewUrls();
        if ( Object.keys( newUrls ).length > 0 ) {

            this.urls = newUrls;

        }

    });

    private getFileName( id: string, file: IDownloadEntry, headers: Record<string, string>, mimeType?: string ): string {

        const extension = valueWithFallbacks(
            getFileNameFromHeader( headers ),
            getFileTypeFromUrl( file.url ),
            mimeType ? mime.getExtension( mimeType ) : ''
        );
        if ( file.name ) {

            if ( extension && !file.name.toLowerCase().endsWith( `.${extension.toLowerCase()}` )) {

                return `${file.name}.${extension}`;

            }

            return file.name;

        }

        return extension ? `${id}.${extension}` : id;

    }

    private async downloadFiles( ids: string[] ): Promise<InputWithoutMeta[]> {

        const maxConcurrentDownloads = 3;
        const files = new Array<InputWithoutMeta>( ids.length );
        let nextIndex = 0;
        const worker = async (): Promise<void> => {

            while ( nextIndex < ids.length ) {

                const index = nextIndex++;
                files[index] = await this.downloadSingleFile( ids[index] );

            }

        };
        const workerCount = Math.min( maxConcurrentDownloads, ids.length );
        await Promise.all( Array.from({ length: workerCount }, () => worker()));
        return files;

    }

    public async downloadBatches(
        fileName: string,
        getNextUrls: () => Promise<TDownloadEntryMap | undefined>
    ): Promise<void> {

        nProgress.start();

        try {

            const files: InputWithoutMeta[] = [];
            while ( true ) {

                const ids = Object.keys( this.urls );
                if ( ids.length > 0 ) {

                    files.push( ...await this.downloadFiles( ids ));

                }
                const nextUrls = await getNextUrls();
                if ( !nextUrls ) {

                    break;

                }
                this.urls = nextUrls;

            }
            if ( files.length === 0 ) {

                return;

            }
            const blob = await downloadZip( files ).blob();
            const extension = blob.type ? mime.getExtension( blob.type ) : undefined;
            this.triggerDownload( blob, extension ? `${fileName}.${extension}` : fileName );
            this.urls = {};

        } finally {

            nProgress.done();

        }

    }

    public async download( fileName: string ): Promise<void> {

        nProgress.start();

        try {

            const ids = Object.keys( this.urls );
            if ( ids.length === 0 ) {

                return;

            }

            if ( ids.length === 1 ) {

                const id = ids[0];
                const { name, blob } = await this.downloadSingleFileAsBlob( id );

                this.triggerDownload( blob, name );

            } else {

                const blob = await downloadZip( await this.downloadFiles( ids )).blob();
                const extension = blob.type ? mime.getExtension( blob.type ) : undefined;
                const zipName = extension ? `${fileName}.${extension}` : fileName;

                this.triggerDownload( blob, zipName );

            }

            this.urls = {};

        } finally {

            nProgress.done();

        }

    }

    private triggerDownload( blob: Blob, name: string ): void {

        const link = document.createElement( 'a' );
        const objectUrl = URL.createObjectURL( blob );
        link.href = objectUrl;
        link.download = name;
        document.body.appendChild( link );
        link.click();
        document.body.removeChild( link );
        window.setTimeout(() => {

            URL.revokeObjectURL( objectUrl );

        }, 1000 );

    }

}
